import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { useStripe, useElements, CardNumberElement, CardCvcElement, CardExpiryElement } from "@stripe/react-stripe-js";
import { SubmitButton, ClickButton, TextInput, SelectCountry } from "components/FormElements";
import { API } from "aws-amplify";
import { useFormFields } from "libs/fields";
import { onError } from "libs/error";
import Header from "components/Header";
import SubHeader from "components/SubHeader";
import Footer from "components/Footer";

type Error = {
  response?: { data: { error: string } };
};

export default function RedeemTrade(props: any) {
  const { t, i18n } = useTranslation();
  const { cartId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [ccNumber, setCCNumber] = useState(false);
  const [ccDate, setCCDate] = useState(false);
  const [ccCVC, setCCCVC] = useState(false);
  const [paymentIntent, setPaymentintent] = useState(Object || null);
  const [errorMessage, setErrorMessage] = useState<JSX.Element>();
  const [fields, handleFieldChange] = useFormFields({
    countryCode: "",
    postalCode: "",
    ccNumber: false,
    ccDate: false,
    ccCVC: false
  });
  const stripe = useStripe();
  const elements = useElements();

  const handleCancel = async (event: any) => {
    event.preventDefault();
    setLoading(true);
    try {
      await API.del("activations", "/activations", {
        body: {
          cartId: cartId
        }
      });
      setLoading(false);
      navigate("/");
    } catch (e) {
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setLoading(false);
      console.error(e);
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setLoading(true);
    let activation = paymentIntent;
    try {
      if (Object.keys(paymentIntent).length === 0) {
        console.log("getting payment intent");
        activation = await API.post("activations", "/activations/fee", {
          body: {
            country_code: fields.countryCode,
            postal_code: fields.postalCode,
            cartId: cartId
          }
        });
        setPaymentintent(activation);
      }

      const creditCard: StripeCardNumberElement = elements!.getElement(CardNumberElement)!;
      const charge = await stripe?.confirmCardPayment(activation.client_secret, {
        payment_method: {
          card: creditCard,
          billing_details: {
            address: {
              country: fields.countryCode,
              postal_code: fields.postalCode
            },
            email: location.state.email
          }
        }
      });
      if (charge?.error) {
        console.error(charge.error);
        throw new Error(charge.error.decline_code || charge.error.code);
      } else if (charge?.paymentIntent) {
        await API.post("activations", `/activations/submit/${cartId}`, {});
        await API.post("whitelabel", "/buy", {
          body: {
            cartId: cartId,
            orderId: cartId
          }
        });
      }

      console.log(charge);
      setLoading(false);
      navigate(`/activation/processing/${cartId}`);
    } catch (e: any) {
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setLoading(false);
      console.error(e);
    }
  };

  function validateForm() {
    return fields.postalCode.length > 1 && fields.countryCode.length === 2 && ccNumber && ccDate && ccCVC;
  }

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4"
        }
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a"
      }
    }
  };

  elements?.getElement(CardNumberElement)?.on("change", function (event) {
    if (event.complete) setCCNumber(true);
    else setCCNumber(false);
  });

  elements?.getElement(CardExpiryElement)?.on("change", function (event) {
    if (event.complete) setCCDate(true);
    else setCCDate(false);
  });

  elements?.getElement(CardCvcElement)?.on("change", function (event) {
    if (event.complete) setCCCVC(true);
    else setCCCVC(false);
  });

  return (
    <div className="min-h-screen flex flex-col" data-reactid={cartId}>
      <Header />
      <SubHeader heading={t("local.redeem.title.main").toString()} />
      <main className="md:px-0 flex-grow relative">
        <form className="container mx-auto max-w-md flex flex-col justify-center space-y-4 pb-20" onSubmit={handleSubmit}>
          <h2 className="text-3xl text-black text-center pt-20 mb-2.5">{t("local.redeem.activation.label.tradeFee")}</h2>
          <SelectCountry
            handleChange={handleFieldChange}
            name="countryCode"
            active={fields.countryCode}
            language={i18n.resolvedLanguage}
            label={t("local.redeem.activation.label.country")}
          />
          <TextInput
            handleChange={handleFieldChange}
            name="postalCode"
            value={fields.postalCode}
            label={t("local.redeem.activation.label.postalCode")}
          />
          <span className="font-body appearance-none block w-full text-sm pt-3.5 pb-3.5 px-3 z-0 border border-ct-grey focus:outline-none focus:border-ct-orange focus:shadow-inner-1 focus:shadow-ct-orange">
            <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
          </span>
          <span className="font-body appearance-none block w-full text-sm pt-3.5 pb-3.5 px-3 z-0 border border-ct-grey focus:outline-none focus:border-ct-orange focus:shadow-inner-1 focus:shadow-ct-orange">
            <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
          </span>
          <span className="font-body appearance-none block w-full text-sm pt-3.5 pb-3.5 px-3 z-0 border border-ct-grey focus:outline-none focus:border-ct-orange focus:shadow-inner-1 focus:shadow-ct-orange">
            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
          </span>
          <SubmitButton disabled={!validateForm() || loading} loading={loading} label={t("local.redeem.activation.label.payTrade")} />
          <ClickButton label={t("cancel")} handleClick={handleCancel} />
          {errorMessage}
        </form>
      </main>
      <Footer />
    </div>
  );
}
